import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { purchaseActions } from '../../action/purchaseActions';
import { 
    Trash2, 
    Download,
    Printer,
    ChevronLeft,
    ChevronRight
} from 'feather-icons-react/build/IconComponents';

const PaymentHistory = ({ purchaseId, onPaymentDeleted }) => {
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [purchase, setPurchase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Fetch payment history
    const fetchPayments = async (page = 1) => {
        try {
            setLoading(true);
            const result = await purchaseActions.getPaymentHistory(purchaseId, page, pagination.limit);
            
            if (result.success) {
                setPayments(result.data.payments || []);
                setSummary(result.data.summary || null);
                setPurchase(result.data.purchase || null);
                
                if (result.data.pagination) {
                    setPagination(result.data.pagination);
                }
            } else {
                Swal.fire('Error', result.message || 'Failed to load payment history', 'error');
            }
        } catch (error) {
            console.error('Error fetching payment history:', error);
            Swal.fire('Error', 'Failed to load payment history', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (purchaseId) {
            fetchPayments(1);
        }
    }, [purchaseId]);

    // Handle delete payment - FIXED VERSION with proper event prevention
    const handleDeletePayment = (e, payment) => {
        // IMPORTANT: Stop event propagation to prevent form submission
        e.preventDefault();
        e.stopPropagation();
        
        Swal.fire({
            title: 'Delete Payment?',
            html: `
                <div class="text-start">
                    <p>Are you sure you want to delete this payment?</p>
                    <ul>
                        <li><strong>Date:</strong> ${dayjs(payment.date).format('DD-MM-YYYY')}</li>
                        <li><strong>Amount:</strong> ₹${payment.amount.toFixed(4)}</li>
                        <li><strong>Method:</strong> ${payment.method || 'N/A'}</li>
                    </ul>
                    <p class="text-danger">This action cannot be undone and will update the purchase balance.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, keep it',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const deleteResult = await purchaseActions.deletePayment(payment.id);
                    
                    if (!deleteResult.success) {
                        throw new Error(deleteResult.message || 'Failed to delete payment');
                    }
                    
                    return deleteResult;
                } catch (error) {
                    Swal.showValidationMessage(`Error: ${error.message}`);
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                const deleteResult = result.value;
                
                // Update parent component FIRST
                if (onPaymentDeleted) {
                    onPaymentDeleted(deleteResult.data);
                }
                
                // Refresh payment list
                fetchPayments(pagination.page);
                
                // Show success message that REQUIRES user to click OK
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Payment has been deleted successfully.',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#3085d6'
                });
            }
        });
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        fetchPayments(pageNumber);
    };

    // Get method badge color
    const getMethodBadge = (method) => {
        switch (method?.toLowerCase()) {
            case 'cash': return 'badge bg-success';
            case 'bank_transfer': return 'badge bg-info';
            case 'cheque': return 'badge bg-warning';
            case 'credit_card': return 'badge bg-primary';
            case 'upi': return 'badge bg-secondary';
            default: return 'badge bg-light text-dark';
        }
    };

    // Format method name
    const formatMethod = (method) => {
        const methods = {
            'cash': 'Cash',
            'bank_transfer': 'Bank Transfer',
            'cheque': 'Cheque',
            'credit_card': 'Credit Card',
            'upi': 'UPI'
        };
        return methods[method] || method;
    };

    if (loading && payments.length === 0) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading payment history...</p>
            </div>
        );
    }

    return (
        <div className="payment-history-container">
            {/* Payment Summary Cards */}
            {summary && purchase && (
                <div className="row mb-3">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body py-2">
                                <small>Total Payments</small>
                                <h5 className="mb-0">{summary.total_payments}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body py-2">
                                <small>Total Paid</small>
                                <h5 className="mb-0">₹{summary.total_amount?.toFixed(4)}</h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body py-2">
                                <small>Last Payment</small>
                                <h5 className="mb-0">
                                    {summary.last_payment_date 
                                        ? dayjs(summary.last_payment_date).format('DD-MM-YYYY')
                                        : 'N/A'}
                                </h5>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className={`card ${
                            purchase.payment_status === 'paid' ? 'bg-success' :
                            purchase.payment_status === 'partial' ? 'bg-warning' : 'bg-danger'
                        } text-white`}>
                            <div className="card-body py-2">
                                <small>Status</small>
                                <h5 className="mb-0 text-uppercase">{purchase.payment_status}</h5>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment History Table */}
            <div className="card">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <i className="fas fa-history me-2"></i>
                        Payment History
                    </h6>
                    <div>
                        <button className="btn btn-sm btn-outline-primary me-1">
                            <Download size={14} className="me-1" />
                            Export
                        </button>
                        <button className="btn btn-sm btn-outline-secondary">
                            <Printer size={14} className="me-1" />
                            Print
                        </button>
                    </div>
                </div>
                <div className="card-body p-0">
                    {payments.length === 0 ? (
                        <div className="text-center p-4">
                            <p className="text-muted mb-0">No payment records found</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Amount (₹)</th>
                                            <th>Method</th>
                                            <th>Reference</th>
                                            <th>Notes</th>
                                            <th>Created By</th>
                                            <th>Created At</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment, index) => (
                                            <tr key={payment.id}>
                                                <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                                                <td>
                                                    <strong>{dayjs(payment.date).format('DD-MM-YYYY')}</strong>
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-success">
                                                        ₹{payment.amount.toFixed(4)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={getMethodBadge(payment.method)}>
                                                        {formatMethod(payment.method)}
                                                    </span>
                                                </td>
                                                <td>
                                                    {payment.reference ? (
                                                        <span className="text-muted">{payment.reference}</span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {payment.notes ? (
                                                        <span title={payment.notes}>
                                                            {payment.notes.length > 20 
                                                                ? `${payment.notes.substring(0, 20)}...` 
                                                                : payment.notes}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="badge bg-info">
                                                        {payment.created_by || 'Admin'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {dayjs(payment.created_at).format('DD-MM-YYYY HH:mm')}
                                                    </small>
                                                </td>
                                                <td>
                                                    <button
                                                        type="button" // IMPORTANT: Set type to "button" to prevent form submission
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={(e) => handleDeletePayment(e, payment)}
                                                        title="Delete Payment"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-light">
                                        <tr>
                                            <td colSpan="2" className="text-end"><strong>Total:</strong></td>
                                            <td colSpan="7">
                                                <strong className="text-success">
                                                    ₹{summary?.total_amount?.toFixed(4) || '0.0000'}
                                                </strong>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-2 border-top">
                                    <small className="text-muted">
                                        Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                        {pagination.total} payments
                                    </small>
                                    <nav>
                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                >
                                                    <ChevronLeft size={14} />
                                                </button>
                                            </li>
                                            
                                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                let pageNumber;
                                                if (pagination.totalPages <= 5) {
                                                    pageNumber = i + 1;
                                                } else if (pagination.page <= 3) {
                                                    pageNumber = i + 1;
                                                } else if (pagination.page >= pagination.totalPages - 2) {
                                                    pageNumber = pagination.totalPages - 4 + i;
                                                } else {
                                                    pageNumber = pagination.page - 2 + i;
                                                }
                                                
                                                return (
                                                    <li
                                                        key={pageNumber}
                                                        className={`page-item ${pagination.page === pageNumber ? 'active' : ''}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(pageNumber)}
                                                        >
                                                            {pageNumber}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                            
                                            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                >
                                                    <ChevronRight size={14} />
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

PaymentHistory.propTypes = {
    purchaseId: PropTypes.number.isRequired,
    onPaymentDeleted: PropTypes.func
};

export default PaymentHistory;